import React, { ReactNode, useCallback, useRef } from "react";
import { ErrorBoundary as ReactErrorBoundary } from "react-error-boundary";
import { useUser } from "../../context/UserContext";
import { logPortalErrorGlobal, markAsBoundaryHandled } from "../../utils/errorLogger";
import { ErrorFallBackUI } from "./ErrorFallBackUI";

interface ErrorBoundaryProps {
  children: ReactNode;
}

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
  const { userId } = useUser();
  const lastErrorRef = useRef<{ message: string; timestamp: number } | null>(null);

  const logPortalError = useCallback(
   async (error: Error, errorInfo: React.ErrorInfo) => {
    // Mark synchronously before any await so window.onunhandledrejection and
    // the window.onerror capture handler both skip navigation / overlay for
    // this error (React 18 dev mode re-fires boundary-caught errors globally).
    markAsBoundaryHandled(error);

    const errorMessage = error.message;
    const errorTimestamp = Date.now();

    if (lastErrorRef.current) {
      const { message, timestamp } = lastErrorRef.current;
      if (message === errorMessage && errorTimestamp - timestamp < 3000) {
        return;
      }
    }
    lastErrorRef.current = { message: errorMessage, timestamp: errorTimestamp };

    await logPortalErrorGlobal({
      errorSummary: error.message,
      errorDetails: errorInfo.componentStack ?? "",
      userId: userId,
    });
    },
    [userId]
  );

  return (
    <ReactErrorBoundary  onError={logPortalError} fallbackRender={({ error, resetErrorBoundary }) => (
      <ErrorFallBackUI error={error} resetErrorBoundary={resetErrorBoundary}/> )} >
      {children}
    </ReactErrorBoundary>
  );
};

export default ErrorBoundary;
